import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  Stack,
  StatusChip,
  TextField,
  Tooltip,
  Typography,
} from '@munifocus/ui';
import { canMarkUp, type Evidence, type TimelinePhoto } from '@munifocus/contracts-gov';
import {
  useCreateMarkup,
  useCreatePair,
  usePhotoTimeline,
  useRemovePair,
} from '../../lib/evidence-markup-api';
import { formatDateTime } from '../../lib/relative-time';
import { BeforeAfterView } from './BeforeAfterView';
import { MarkupEditor } from './MarkupEditor';

function gps(p: Evidence, none: string): string {
  return p.latitude != null && p.longitude != null
    ? `${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}`
    : none;
}

function PhotoCard({
  photo,
  canEdit,
  selected,
  onToggle,
  onMarkUp,
}: {
  photo: TimelinePhoto;
  canEdit: boolean;
  selected: boolean;
  onToggle: () => void;
  onMarkUp: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Stack
      component="li"
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      data-testid="timeline-photo"
      sx={{ listStyle: 'none', py: 1, borderTop: 1, borderColor: 'divider' }}
    >
      <Box
        component="a"
        href={photo.downloadUrl}
        target="_blank"
        rel="noreferrer"
        sx={{ flexShrink: 0, lineHeight: 0 }}
      >
        <Box
          component="img"
          src={photo.downloadUrl}
          alt={photo.originalFilename}
          sx={{ width: 160, maxWidth: '100%', height: 120, objectFit: 'cover', borderRadius: 1 }}
        />
      </Box>
      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {formatDateTime(photo.capturedAt, i18n.language)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('gov.evidenceMarkup.takenBy', {
            name: photo.takenByName ?? t('gov.evidenceMarkup.unknownOfficer'),
          })}
          {' · '}
          {gps(photo, t('gov.evidenceMarkup.noGps'))}
        </Typography>
        {photo.note && <Typography variant="body2">{photo.note}</Typography>}
        <Box>
          {photo.integrity === 'recorded' ? (
            <Tooltip title={t('gov.evidenceMarkup.digestTooltip', { digest: photo.sha256 ?? '' })}>
              <span>
                <StatusChip status="success" label={t('gov.evidenceMarkup.integrityRecorded')} />
              </span>
            </Tooltip>
          ) : (
            <StatusChip status="warning" label={t('gov.evidenceMarkup.integrityUnrecorded')} />
          )}
        </Box>
        {photo.markups.length > 0 && (
          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
            {photo.markups.map((m, i) => (
              <li key={m.id}>
                <Link href={m.downloadUrl} target="_blank" rel="noreferrer">
                  {t('gov.evidenceMarkup.markupLink', { n: i + 1 })}
                </Link>
                <Typography component="span" variant="caption" color="text.secondary">
                  {' · '}
                  {t('gov.evidenceMarkup.markupDigest', { digest: (m.sha256 ?? '').slice(0, 12) })}
                </Typography>
              </li>
            ))}
          </Stack>
        )}
        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {canMarkUp(photo) && (
              <Button
                size="small"
                startIcon={<Icons.Pencil size={iconSize.sm} />}
                onClick={onMarkUp}
              >
                {t('gov.evidenceMarkup.markUp')}
              </Button>
            )}
            <FormControlLabel
              control={<Checkbox size="small" checked={selected} onChange={onToggle} />}
              label={t('gov.evidenceMarkup.selectForPair')}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

/**
 * Every photo on the file, chronologically under the site visit it was taken
 * during, with who took it, where, and its integrity record. Marking up and
 * before/after pairing live here. Renders nothing unless the municipality
 * has evidence markup switched on.
 */
export function PhotoTimelinePanel({ fileId, canEdit }: { fileId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const timeline = usePhotoTimeline(fileId);
  const markup = useCreateMarkup(fileId);
  const pair = useCreatePair(fileId);
  const remove = useRemovePair(fileId);
  const [editing, setEditing] = useState<TimelinePhoto | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [pairing, setPairing] = useState(false);
  const [caption, setCaption] = useState('');

  const photos = useMemo(
    () => new Map((timeline.data?.groups ?? []).flatMap((g) => g.photos).map((p) => [p.id, p])),
    [timeline.data],
  );

  if (!timeline.data?.enabled) return null;
  const { groups, pairs } = timeline.data;

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2)));

  // The earlier capture is "before".
  const ordered = selected
    .map((id) => photos.get(id))
    .filter((p): p is TimelinePhoto => !!p)
    .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="overline" component="h2">
            {t('gov.evidenceMarkup.title')}
          </Typography>
          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              disabled={ordered.length !== 2}
              onClick={() => setPairing(true)}
            >
              {t('gov.evidenceMarkup.pairSelected')}
            </Button>
          )}
        </Stack>

        {groups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.evidenceMarkup.empty')}
          </Typography>
        )}

        {groups.map((g) => (
          <Box key={g.visit?.id ?? 'none'}>
            <Typography variant="subtitle2" component="h3">
              {g.visit
                ? t('gov.evidenceMarkup.visitHeading', {
                    date: g.visit.startedAt ? formatDateTime(g.visit.startedAt, i18n.language) : '',
                    officer: g.visit.officerName ?? t('gov.evidenceMarkup.unknownOfficer'),
                  })
                : t('gov.evidenceMarkup.noVisit')}
            </Typography>
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {g.photos.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  canEdit={canEdit}
                  selected={selected.includes(p.id)}
                  onToggle={() => toggle(p.id)}
                  onMarkUp={() => setEditing(p)}
                />
              ))}
            </Box>
          </Box>
        ))}

        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {t('gov.evidenceMarkup.pairsTitle')}
          </Typography>
          {pairs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('gov.evidenceMarkup.noPairs')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {pairs.map((p) => {
                const before = photos.get(p.beforeEvidenceId);
                const after = photos.get(p.afterEvidenceId);
                if (!before || !after) return null;
                return (
                  <Stack key={p.id} spacing={1} data-testid="evidence-pair">
                    {p.caption && <Typography sx={{ fontWeight: 600 }}>{p.caption}</Typography>}
                    <BeforeAfterView before={before} after={after} />
                    {canEdit && (
                      <Box>
                        <Button
                          size="small"
                          color="error"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(p.id)}
                        >
                          {t('gov.evidenceMarkup.removePair')}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>
      </Stack>

      {editing && (
        <MarkupEditor
          photo={editing}
          saving={markup.isPending}
          onClose={() => setEditing(null)}
          onSave={(annotations, note) =>
            markup.mutate(
              { evidenceId: editing.id, annotations, note },
              { onSuccess: () => setEditing(null) },
            )
          }
        />
      )}

      <Dialog open={pairing} onClose={() => setPairing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('gov.evidenceMarkup.pairTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.evidenceMarkup.pairIntro')}
            </Typography>
            <TextField
              label={t('gov.evidenceMarkup.caption')}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPairing(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={ordered.length !== 2 || pair.isPending}
            onClick={() =>
              pair.mutate(
                {
                  beforeEvidenceId: ordered[0].id,
                  afterEvidenceId: ordered[1].id,
                  ...(caption.trim() ? { caption: caption.trim() } : {}),
                },
                {
                  onSuccess: () => {
                    setPairing(false);
                    setSelected([]);
                    setCaption('');
                  },
                },
              )
            }
          >
            {t('gov.evidenceMarkup.savePair')}
          </Button>
        </DialogActions>
      </Dialog>
    </HairlineCard>
  );
}
